
import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { jobCategories, jobRegions, jobWorkTypes } from '../../../mocks/jobList';

interface FilterSidebarProps {
  selectedCategory: string;
  selectedRegion: string;
  selectedWorkType: string;
  searchQuery: string;
  onCategoryChange: (v: string) => void;
  onRegionChange: (v: string) => void;
  onWorkTypeChange: (v: string) => void;
  onSearchChange: (v: string) => void;
  onReset: () => void;
}

export default function FilterSidebar({
  selectedCategory,
  selectedRegion,
  selectedWorkType,
  searchQuery,
  onCategoryChange,
  onRegionChange,
  onWorkTypeChange,
  onSearchChange,
  onReset,
}: FilterSidebarProps) {
  return (
    <motion.aside
      initial={{ opacity: 0, x: -30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className="w-72 flex-shrink-0"
    >
      <div className="sticky top-28 space-y-8">
        {/* Search */}
        <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-6">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5 flex items-center justify-center"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="포지션, 회사명 검색..."
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-neon-purple/50 transition-colors"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-bold text-white">필터</h3>
            <button
              onClick={onReset}
              className="text-xs text-gray-500 hover:text-neon-pink transition-colors cursor-pointer whitespace-nowrap"
            >
              초기화
            </button>
          </div>

          {/* Category */}
          <div className="mb-7">
            <h4 className="text-sm font-semibold text-gray-300 mb-3">
              <a href="#category" className="hover:text-neon-purple transition-colors">직무</a>
            </h4>
            <div className="flex flex-wrap gap-2">
              {jobCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => onCategoryChange(cat)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    selectedCategory === cat
                      ? 'bg-gradient-neon text-white shadow-neon-purple'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/5'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Region */}
          <div className="mb-7">
            <h4 className="text-sm font-semibold text-gray-300 mb-3">
              <a href="#region" className="hover:text-neon-purple transition-colors">지역</a>
            </h4>
            <div className="flex flex-wrap gap-2">
              {jobRegions.map((region) => (
                <button
                  key={region}
                  onClick={() => onRegionChange(region)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    selectedRegion === region
                      ? 'bg-gradient-neon text-white shadow-neon-purple'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/5'
                  }`}
                >
                  {region}
                </button>
              ))}
            </div>
          </div>

          {/* Work Type */}
          <div className="mb-7">
            <h4 className="text-sm font-semibold text-gray-300 mb-3">
              <a href="#worktype" className="hover:text-neon-purple transition-colors">근무형태</a>
            </h4>
            <div className="flex flex-wrap gap-2">
              {jobWorkTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => onWorkTypeChange(type)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    selectedWorkType === type
                      ? 'bg-gradient-neon text-white shadow-neon-purple'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/5'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-gradient-purple rounded-2xl border border-white/10 p-6">
          <h4 className="text-sm font-bold text-white mb-4">
            <a href="#stats" className="hover:text-neon-purple transition-colors">실시간 현황</a>
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">총 공고 수</span>
              <span className="text-sm font-bold text-neon-purple">1,247</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">오늘 등록</span>
              <span className="text-sm font-bold text-neon-pink">+24</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">활성 지원자</span>
              <span className="text-sm font-bold text-neon-green">3,891</span>
            </div>
          </div>
        </div>
      </div>
    </motion.aside>
  );
}
